import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { colors } from './ui/styles'
import { useNotificationUnreadCount, useNotificationList } from '../hooks/use-notifications'
import type { Notification, NotificationSeverity } from '@alpha-edge/shared'

const SEVERITY_COLORS: Record<NotificationSeverity, string> = {
  critical: '#dc2626',
  warning: '#f59e0b',
  info: '#9ca3af',
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs} hr ago`
  return `${Math.floor(hrs / 24)} days ago`
}

function NotificationDropdown({ onClose, refreshBadge }: { onClose: () => void; refreshBadge: () => void }) {
  const navigate = useNavigate()
  const { rows, unreadCount, loading, markRead, markAllRead } = useNotificationList()

  const handleMarkAllRead = async () => {
    await markAllRead()
    refreshBadge()
  }

  const handleRowClick = async (row: Notification) => {
    if (row.readAt === null) {
      await markRead([row.id])
      refreshBadge()
    }
    if (row.link) {
      navigate(row.link)
      onClose()
    }
  }

  return (
    <div
      data-testid="notification-dropdown"
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        width: '360px',
        maxHeight: '480px',
        overflowY: 'auto',
        background: '#fff',
        border: `1px solid ${colors.border}`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        zIndex: 100,
        borderRadius: '4px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          borderBottom: `1px solid ${colors.border}`,
          position: 'sticky',
          top: 0,
          background: '#fff',
        }}
      >
        <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>Notifications</span>
        <button
          data-testid="mark-all-read"
          onClick={() => void handleMarkAllRead()}
          disabled={unreadCount === 0}
          style={{
            background: 'none',
            border: 'none',
            cursor: unreadCount === 0 ? 'default' : 'pointer',
            color: unreadCount === 0 ? colors.textMuted : colors.accent,
            fontSize: '0.75rem',
            textDecoration: 'underline',
          }}
        >
          Mark all read
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div style={{ padding: '1rem', textAlign: 'center', color: colors.textMuted, fontSize: '0.875rem' }}>
          Loading...
        </div>
      ) : rows.length === 0 ? (
        <div
          data-testid="empty-state"
          style={{ padding: '1.5rem', textAlign: 'center', color: colors.textMuted, fontSize: '0.875rem' }}
        >
          No notifications yet
        </div>
      ) : (
        rows.map((row) => (
          <div
            key={row.id}
            data-testid={`notification-row-${row.id}`}
            onClick={() => void handleRowClick(row)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              cursor: 'pointer',
              borderBottom: `1px solid ${colors.border}`,
              background: row.readAt === null ? '#fafafa' : '#fff',
            }}
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: SEVERITY_COLORS[row.severity],
                flexShrink: 0,
                marginTop: '4px',
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {row.title}
              </div>
              {row.body && (
                <div
                  style={{
                    color: colors.textMuted,
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    marginTop: '2px',
                  }}
                >
                  {row.body}
                </div>
              )}
              <div style={{ color: colors.textMuted, fontSize: '0.6875rem', marginTop: '4px' }}>
                {relativeTime(row.createdAt)}
              </div>
            </div>
            {row.readAt === null && (
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: colors.accent,
                  flexShrink: 0,
                  marginTop: '4px',
                }}
              />
            )}
          </div>
        ))
      )}

      <div
        style={{
          padding: '0.5rem 1rem',
          borderTop: `1px solid ${colors.border}`,
          textAlign: 'center',
          position: 'sticky',
          bottom: 0,
          background: '#fff',
        }}
      >
        <a
          href="/preferences/notifications"
          data-testid="view-all-link"
          onClick={(e) => {
            e.preventDefault()
            navigate('/preferences/notifications')
            onClose()
          }}
          style={{ color: colors.accent, fontSize: '0.8125rem', textDecoration: 'underline' }}
        >
          View all
        </a>
      </div>
    </div>
  )
}

export function NotificationBell() {
  const { count, refresh } = useNotificationUnreadCount()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => setOpen(false), [])
  const toggle = useCallback(() => setOpen((v) => !v), [])

  useEffect(() => {
    if (!open) return
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close()
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open, close])

  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, close])

  const badgeLabel = count >= 100 ? '99+' : count > 0 ? String(count) : null

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        onClick={toggle}
        aria-label="Notifications"
        data-testid="notification-bell"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          color: colors.accent,
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {badgeLabel && (
          <span
            data-testid="notification-badge"
            style={{
              position: 'absolute',
              top: '-6px',
              right: '-8px',
              minWidth: '16px',
              height: '16px',
              lineHeight: '16px',
              borderRadius: '8px',
              background: '#dc2626',
              color: '#fff',
              fontSize: '0.625rem',
              fontWeight: 700,
              textAlign: 'center',
              padding: '0 3px',
            }}
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {open && <NotificationDropdown onClose={close} refreshBadge={refresh} />}
    </div>
  )
}
